import React from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useTranslation } from "react-i18next";

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalCount, 
  onPageChange, 
  nextPage, 
  previousPage,
  pageSize = 10, // default page size to calculate 'showing x'
  stacked = false
}) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  if (!totalCount || totalCount === 0) return null;

  // Calculate the 'Showing X' value (Cumulative: current items + previous pages)
  // Example: Page 2 with 10 items/page => showing 20 of total
  const showingUntil = Math.min(currentPage * pageSize, totalCount);

  // Function to generate the 5 page numbers (Dynamic Window)
  const getPageNumbers = () => {
    let pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // If total pages are 5 or less, show all
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first and last, and a window around current page
      if (currentPage <= 3) {
        pages = [1, 2, 3, 4, totalPages];
      } else if (currentPage >= totalPages - 2) {
        pages = [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        pages = [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
      }
    }
    return pages;
  };

  const buttonStyle = (isActive, isDisabled) => ({
    border: "1px solid #e0e4ec",
    backgroundColor: isActive ? "#730595ff" : isDisabled ? "#f8f9fa" : "#fff",
    color: isActive ? "#fff" : isDisabled ? "#aab4be" : "#730595ff",
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: isDisabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    fontSize: "13px",
    fontWeight: isActive ? "700" : "500",
    padding: 0,
    outline: "none",
    boxShadow: isActive ? "0 2px 5px rgba(115, 5, 149, 0.2)" : "none"
  });
  const arrowButtonStyle = (isDisabled) => ({
    border: "none", // Removed the border
    backgroundColor: "transparent", // Transparent background
    color: isDisabled ? "#aab4be" : "#730595ff",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: isDisabled ? "not-allowed" : "pointer",
    padding: 0,
    outline: "none",
    transition: "transform 0.2s ease",
    // Slight zoom effect on hover for better UX
    "&:hover": { transform: isDisabled ? "none" : "scale(1.1)" }
  });

  return (
    <div style={{ borderTop: "1px solid #eee", width: "100%" }}>
      
      {stacked ? (
        <>
          {/* Row 1: Buttons */}
          <div className="d-flex justify-content-center align-items-center gap-2 pt-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!previousPage}
              style={arrowButtonStyle(!previousPage)}
            >
              {isAr ? <MdChevronRight size={22} /> : <MdChevronLeft size={22} />}
            </button>

            {getPageNumbers().map((pg, index, array) => (
              <React.Fragment key={pg}>
                {index > 0 && pg - array[index - 1] > 1 && (
                  <span style={{ color: "#aab4be" }}>...</span>
                )}
                <button onClick={() => onPageChange(pg)} style={buttonStyle(currentPage === pg, false)}>
                  {pg}
                </button>
              </React.Fragment>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!nextPage}
              style={arrowButtonStyle(!nextPage)}
            >
              {isAr ? <MdChevronLeft size={22} /> : <MdChevronRight size={22} />}
            </button>
          </div>

          {/* Row 2: Showing Text */}
          <div className="d-flex justify-content-center align-items-center pb-2"
              style={{ color: "#728292", fontSize: "13px", fontWeight: "500" }}>
            {t("pagination.showing") || "Showing"}
            <span style={{ color: "#333", fontWeight: "600", margin: "0 4px" }}>{showingUntil}</span>
            {t("pagination.of") || "of"}
            <span style={{ color: "#333", fontWeight: "600", margin: "0 4px" }}>{totalCount}</span>
            {t("pagination.results") || "results"}
          </div>
        </>
      ) : (
        <div className="d-flex justify-content-between align-items-center mt-3 py-3 px-2">
          <div style={{ color: "#728292", fontSize: "13px", fontWeight: "500" }}>
            {t("pagination.showing") || "Showing"}
            <span style={{ color: "#333", fontWeight: "600", margin: "0 4px" }}>{showingUntil}</span>
            {t("pagination.of") || "of"}
            <span style={{ color: "#333", fontWeight: "600", margin: "0 4px" }}>{totalCount}</span>
            {t("pagination.results") || "results"}
          </div>

          <div className="d-flex align-items-center gap-2">
            <button onClick={() => onPageChange(currentPage - 1)} disabled={!previousPage} style={arrowButtonStyle(!previousPage)}>
              {isAr ? <MdChevronRight size={22} /> : <MdChevronLeft size={22} />}
            </button>

            {getPageNumbers().map((pg, index, array) => (
              <React.Fragment key={pg}>
                {index > 0 && pg - array[index - 1] > 1 && (
                  <span style={{ color: "#aab4be" }}>...</span>
                )}
                <button onClick={() => onPageChange(pg)} style={buttonStyle(currentPage === pg, false)}>
                  {pg}
                </button>
              </React.Fragment>
            ))}

            <button onClick={() => onPageChange(currentPage + 1)} disabled={!nextPage} style={arrowButtonStyle(!nextPage)}>
              {isAr ? <MdChevronLeft size={22} /> : <MdChevronRight size={22} />}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Pagination;